/**
 * Scroll motion layer.
 *
 * Locked brand: this adds movement to the existing look, it does not restyle
 * anything. Everything here is progressive enhancement:
 *   - prefers-reduced-motion  -> no motion at all, content stays fully visible
 *   - JS disabled / fails      -> no elements are ever hidden, page is static
 *
 * Reveals use IntersectionObserver (not ScrollTrigger) so they are immune to
 * layout shifts from lazy-loaded product images, the earlier ScrollTrigger
 * approach cached start positions before images loaded and mid-page grids
 * never fired. ScrollTrigger is kept only for the scrubbed parallax.
 *
 * SCROLLING IS NATIVE. Lenis smooth scroll was removed on 2026-07-30 because it
 * cost more than it gave on every device and nothing here depended on it:
 *
 *   - It registers touchstart/touchmove/touchend with `passive: false`. That
 *     takes the page off the compositor scroll path and routes every finger move
 *     through the main thread, on a site whose home page is over 10000px tall.
 *     `syncTouch` was off, so its touch handler always returned without
 *     preventing anything: the phone paid the whole cost for no effect.
 *   - Smooth wheel is lag by definition. At `duration: 1.1` the viewport trailed
 *     the wheel by 137px after input stopped, which reads as the page pushing
 *     back rather than as smoothness.
 *   - Nothing needed it. There is not one `[data-parallax]` element in any view,
 *     so there is no scrubbed timeline to keep in sync, and reveals are driven by
 *     IntersectionObserver.
 *
 * To bring it back, re-add `new Lenis()` here and hand it to `registerLenis` in
 * ./scroll-lock so overlays can still stop it.
 */
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EASE = 'power3.out';

function hiddenState(kind) {
    switch (kind) {
        case 'left':    return { opacity: 0, x: -48 };
        case 'right':   return { opacity: 0, x: 48 };
        case 'scale':   return { opacity: 0, scale: 0.92, y: 26 };
        case 'heading': return { opacity: 0, y: 26, clipPath: 'inset(0 0 100% 0)' };
        case 'up':
        default:        return { opacity: 0, y: 42 };
    }
}

function shownState(kind, extra) {
    const to = { opacity: 1, x: 0, y: 0, scale: 1, ease: EASE, ...extra };
    if (kind === 'heading') to.clipPath = 'inset(0 0 0% 0)';
    return to;
}

export function initMotion() {
    // Storefront-only layer. The owner admin has no reveals/parallax to play, and
    // Lenis smooth-scroll captures every wheel/trackpad event page-wide, which kills
    // native scrolling inside the admin sidebar nav, notification lists, dropdowns
    // and modals (scrollbar drag keeps working, the wheel does not). Never start it
    // on admin: the admin shell owns plain native scroll.
    if (document.querySelector('.admin-shell')) {
        return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return; // honor the OS setting: no motion, nothing hidden
    }

    initReveals();
    initCounters();
}

/*
 | Count-up on the home page's trust and stat numbers.
 |
 | The number ships in the HTML at its FINAL value and this counts up TO it.
 | The mockup did the opposite - `<div data-count="8">0</div>` - which renders
 | a page of zeroes to anyone whose JS fails, to every crawler that does not
 | execute it, and to the golden snapshots. Same rule as the reveals: the
 | resting state is the true state, and motion is what is added on top.
 |
 | Arabic-Indic digits are left alone. `parseInt` returns NaN on '٢٠١٨', and a
 | count-up that renders Latin digits mid-animation and Arabic ones at the end
 | is worse than no animation, so the element is simply skipped.
 */
function initCounters() {
    const els = gsap.utils.toArray('[data-count-up]');
    if (!els.length) return;

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);

            const el = entry.target;
            const end = parseInt(el.textContent.replace(/\D/g, ''), 10);
            if (!Number.isFinite(end) || /[^\d\s,]/.test(el.textContent.trim())) return;

            const box = { v: 0 };
            gsap.to(box, {
                v: end,
                duration: 1,
                ease: 'power3.out',
                onUpdate: () => { el.textContent = String(Math.round(box.v)); },
                onComplete: () => { el.textContent = String(end); },
            });
        });
    }, { threshold: 0.5 });

    els.forEach((el) => io.observe(el));
}

function initReveals() {
    // ── Reveals via IntersectionObserver ─────────────────────────────────────
    // Everything still waiting to be revealed. A scroll that jumps PAST an
    // element is swept from here, because the observer cannot see it.
    const pending = new Set();

    const reveal = (el) => {
        if (!pending.has(el)) return;
        pending.delete(el);
        io.unobserve(el);
        playReveal(el);
        if (pending.size === 0) window.removeEventListener('scroll', onScroll);
    };

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
        });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });

    /*
     | The jumped-past sweep.
     |
     | IntersectionObserver fires on threshold CROSSINGS. An element that goes
     | from ratio 0 below the viewport to ratio 0 above it never crosses 0.08,
     | so the callback does not run at all - not with a stale entry, not with
     | isIntersecting false. It simply never fires, and the element stays at
     | opacity 0 for the rest of the session.
     |
     | That happens on an anchor link, on a restored scroll position after
     | back, on Cmd+End, and on a fast flick on a phone. Measured on the Elite
     | landing page: a single scrollTo(bottom) left eight elements invisible.
     |
     | rAF-coalesced and passive, and it removes itself the moment the last
     | element has been revealed, so the steady-state cost is zero.
     */
    let ticking = false;

    function onScroll() {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
            ticking = false;
            pending.forEach((el) => {
                if (el.getBoundingClientRect().top < window.innerHeight) reveal(el);
            });
        });
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    function playReveal(el) {
        if (el.hasAttribute('data-reveal-stagger')) {
            const items = el.querySelectorAll(':scope > *');
            gsap.to(items, {
                ...shownState('up', { duration: 0.7, stagger: 0.09, overwrite: 'auto' }),
            });
        } else {
            const kind = el.getAttribute('data-reveal') || 'up';
            gsap.to(el, {
                ...shownState(kind, {
                    duration: kind === 'heading' ? 1.0 : 0.9,
                    delay: parseFloat(el.getAttribute('data-reveal-delay')) || 0,
                    overwrite: 'auto',
                }),
            });
        }
    }

    // Set the hidden start states, then observe.
    gsap.utils.toArray('[data-reveal]').forEach((el) => {
        gsap.set(el, hiddenState(el.getAttribute('data-reveal') || 'up'));
        pending.add(el);
        io.observe(el);
    });
    gsap.utils.toArray('[data-reveal-stagger]').forEach((group) => {
        const items = group.querySelectorAll(':scope > *');
        if (!items.length) return;
        gsap.set(items, { opacity: 0, y: 36, scale: 0.98 });
        pending.add(group);
        io.observe(group);
    });

    // Nothing to sweep for.
    if (pending.size === 0) window.removeEventListener('scroll', onScroll);

    // ── Subtle parallax drift (scrubbed to scroll) ───────────────────────────
    gsap.utils.toArray('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-parallax')) || 0.1;
        gsap.to(el, {
            yPercent: speed * 100,
            ease: 'none',
            scrollTrigger: {
                trigger: el.closest('[data-parallax-scope]') || el,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.6,
            },
        });
    });

    window.addEventListener('load', () => ScrollTrigger.refresh());
}
